import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Tooltip,
  Button,
  AppBar,
  Toolbar,
  Slide,
  useScrollTrigger
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, KeyboardArrowUp as UpIcon, Home as HomeIcon, Search as SearchIconAlt, Note as NoteIcon, Settings as SettingsIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Public as PublicIcon, Group as GroupIcon, PhotoLibrary as PhotoLibraryIcon, History as HistoryIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDb, waitForFirebase } from '../services/firebase';
import { collection, query, where, orderBy, getDocs, doc, getDoc, limit, startAfter, updateDoc } from 'firebase/firestore';
import BottomBar from '../components/BottomBar';

// HideOnScroll 組件
function HideOnScroll(props: { children: React.ReactElement }) {
  const trigger = useScrollTrigger();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const isLandscape = typeof window !== 'undefined' ? window.innerHeight < 700 : false;
  
  // 在手機直式模式下不隱藏 AppBar
  if (isMobile && !isLandscape) {
    return props.children;
  }
  
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {props.children}
    </Slide>
  );
}

interface VideoNote {
  id?: string;
  videoId: string;
  title: string;
  videoUrl?: string;
  channelName?: string;
  publishedAt?: string;
  lastWatchedDate?: string;
  lastOpenDate?: string; // 🔧 新增：瀏覽時間字段
  checkpoints?: { [key: string]: string };
  checkpointCount?: number;
  notesCount?: number;
  userId?: string;
  zones?: string[]; // 🔧 新增：zones 字段
  userLastReadAt?: any; // 🔧 新增：用戶最後閱讀時間
  thumbnail?: string; // 🔧 新增：縮圖字段
  notes?: Array<{
  keyNumber: number;
  note: string;
  timestamp: any;
  timecode: string;
  }>;
  earliestNote?: any;
  addedDate?: any;
  timestamp?: any;
  titleMatch?: boolean; // 🔧 新增：標記是否為標題匹配
}

// 判斷影片來源的函數
const getVideoSource = (videoUrl: string): string => {
  if (!videoUrl) return 'YT';
  
  if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
    return 'YT'; // YouTube
  } else if (videoUrl.includes('blob:') || videoUrl.startsWith('/') || videoUrl.includes('localhost')) {
    return 'LF'; // Local File
  } else if (videoUrl.includes('drive.google.com') || videoUrl.includes('dropbox.com') || videoUrl.includes('onedrive.com')) {
    return 'CD'; // Cloud Drive
  } else {
    return 'YT'; // 預設為 YouTube
  }
};

// 渲染影片來源圖標的函數
const renderVideoSourceIcon = (videoUrl: string) => {
  const source = getVideoSource(videoUrl);
  
  switch (source) {
    case 'YT':
      return (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '2px',
            backgroundColor: '#ff0000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: 1
          }}
        >
          ▶
        </Box>
      );
    case 'LF':
      return (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '2px',
            backgroundColor: '#4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            marginRight: 1
          }}
        >
          LF
        </Box>
      );
    case 'CD':
      return (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '2px',
            backgroundColor: '#2196f3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            marginRight: 1
          }}
        >
          CD
        </Box>
      );
    default:
      return null;
  }
};

interface NotesPageProps {
  pageState?: any;
  onStateChange?: (newState: any) => void;
  onNavigateToMain?: (data: any) => void;
  isEmbedded?: boolean;
}

const NotesPage: React.FC<NotesPageProps> = ({ 
  pageState, 
  onStateChange, 
  onNavigateToMain,
  isEmbedded = false 
}) => {
  // 调试日志已移除
  const { user: authUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState<VideoNote[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔧 修復：使用與 MainLayout 一致的 menuItems，避免選單內容變化
  const menuItems = [
    {
      key: 'settings',
      label: '設定',
      icon: <SettingsIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        console.log('開啟設定視窗');
        // TODO: 實作設定功能
      }
    },
    {
      key: 'communityPublic',
      label: '社群公開',
      icon: <PublicIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 manage 頁面並設置為社群公開分頁
        if (isEmbedded && onPageChange) {
          onPageChange('manage');
          setTimeout(() => {
            window.history.replaceState({}, '', '/manage?section=communityPublic');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }, 100);
        } else {
          navigate('/manage?section=communityPublic');
        }
      }
    },
    {
      key: 'teamPoint',
      label: '群組影片',
      icon: <GroupIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 manage 頁面並設置為群組影片分頁
        if (isEmbedded && onPageChange) {
          onPageChange('manage');
          setTimeout(() => {
            window.history.replaceState({}, '', '/manage?section=teamPoint');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }, 100);
        } else {
          navigate('/manage?section=teamPoint');
        }
      }
    },
    {
      key: 'expertNotes',
      label: '專家筆記',
      icon: <NoteIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 notes 頁面
        if (isEmbedded && onPageChange) {
          onPageChange('notes');
        } else {
          navigate('/notes');
        }
      }
    },
    {
      key: 'myVideos',
      label: '我的創作',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 manage 頁面並設置為我的創作分頁
        if (isEmbedded && onPageChange) {
          onPageChange('manage');
          setTimeout(() => {
            window.history.replaceState({}, '', '/manage?section=myVideos');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }, 100);
        } else {
          navigate('/manage?section=myVideos');
        }
      }
    },
    {
      key: 'personalHistory',
      label: '瀏覽記錄',
      icon: <HistoryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 切換到 manage 頁面並設置為瀏覽記錄分頁
        if (isEmbedded && onPageChange) {
          onPageChange('manage');
          setTimeout(() => {
            window.history.replaceState({}, '', '/manage?section=personalHistory');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }, 100);
        } else {
          navigate('/manage?section=personalHistory');
        }
      }
    }
  ];
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  
  // 搜尋相關狀態
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<VideoNote[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // 月份篩選狀態
  const [selectedMonthFilters, setSelectedMonthFilters] = useState<Set<string>>(new Set());
  const [monthFilterOptions, setMonthFilterOptions] = useState<string[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<VideoNote[]>([]);
  
  // 狀態保存相關
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  
  // 🔧 新增：編輯相關狀態
  const [editingNote, setEditingNote] = useState<{videoId: string, keyNumber: number} | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // 移除詳細日誌以提升性能

  // 🔧 修復：處理點擊 NO 播放影片 - 優化跳轉速度
  const handlePlayVideo = (videoNote: VideoNote, targetKeyNumber?: number) => {
    // 调试日志已移除
    
    // 🔧 優化：簡化數據轉換，減少處理時間
    const vbids = videoNote.notes?.map(note => ({
      keyNo: note.keyNumber,
      note: note.note,
      timecode: note.timecode,
      timestamp: note.timestamp
    })) || [];
    
    // 🔧 修復：識別雲端檔案（videoId 以 file_ 開頭或 videoUrl 包含 storage.googleapis.com）
    const isCloudFile = videoNote.videoId.startsWith('file_') || 
                        videoNote.videoUrl?.includes('storage.googleapis.com');
    
    const navigationData: any = {
      vbids: vbids,
      jumpToCheckpoint: targetKeyNumber,
      isFromNotesPage: true,
      videoId: videoNote.videoId,
      firestoreId: videoNote.id, // 🔧 修復：傳遞 firestoreId 以正確載入重點和 Note
      documentId: videoNote.id, // 保留向後兼容
      videoUrl: videoNote.videoUrl, // 🔧 修復：直接傳遞 videoUrl（YouTube 或 GCS URL）
      // 🔧 新增：優化標記，跳過自動滾動和延遲初始化
      skipAutoScroll: true,
      fastLoad: true
    };
    
    // 🔧 修復：如果是雲端檔案，添加額外資訊
    if (isCloudFile) {
      // 调试日志已移除
      navigationData.source = 'cloud_upload';
      navigationData.mode = 'VIDEO'; // 🔧 修復：明確設置 mode
      navigationData.cloudFile = {
        url: videoNote.videoUrl,
        fileName: videoNote.title
      };
    }
    
    // 🚀 優化：立即跳轉，不等待任何處理
    if (isEmbedded && onNavigateToMain) {
      onNavigateToMain(navigationData);
    } else {
      navigate('/v/' + videoNote.videoId, {
        state: navigationData,
        replace: false // 保持歷史記錄
      });
    }
  };

  // 🔧 新增：開始編輯筆記
  const handleStartEdit = (videoId: string, keyNumber: number, currentNote: string) => {
    setEditingNote({ videoId, keyNumber });
    setEditingText(currentNote);
  };

  // 🔧 新增：取消編輯
  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditingText('');
  };

  // 🔧 新增：保存編輯的筆記到 Firestore
  const handleSaveEdit = async () => {
    if (!editingNote || !authUser?.uid || isSaving) return;

    setIsSaving(true);
    try {
      console.log('🔍 開始保存編輯:', {
        videoId: editingNote.videoId,
        keyNumber: editingNote.keyNumber,
        userId: authUser?.uid,
        editingText: editingText
      });

      // 🔧 修復：正確查詢 sharedVbids 文檔
      const sharedVbidsQuery = query(
        collection(getDb(), 'sharedVbids'),
        where('userId', '==', authUser?.uid),
        where('videoId', '==', editingNote.videoId)
      );

      const querySnapshot = await getDocs(sharedVbidsQuery);
      
      console.log('🔍 查詢結果:', {
        querySize: querySnapshot.size,
        videoId: editingNote.videoId,
        userId: authUser?.uid
      });

      if (querySnapshot.empty) {
        console.error('❌ 找不到對應的 sharedVbids 文檔', {
          videoId: editingNote.videoId,
          userId: authUser?.uid
        });
        
        // 🔧 嘗試調試：查詢所有該用戶的 sharedVbids 文檔
        const allUserVbidsQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('userId', '==', authUser?.uid)
        );
        const allUserSnapshot = await getDocs(allUserVbidsQuery);
        console.log('🔍 該用戶的所有 sharedVbids 文檔:', allUserSnapshot.docs.map(doc => ({
          id: doc.id,
          videoId: doc.data().videoId,
          userId: doc.data().userId
        })));
        
        return;
      }

      // 獲取文檔數據
      const docRef = querySnapshot.docs[0].ref;
      const docData = querySnapshot.docs[0].data();
      const vbids = docData.vbids || [];

      console.log('🔍 找到的文檔數據:', {
        docId: docRef.id,
        vbidsCount: vbids.length,
        vbids: vbids.map((vbid: any) => ({
          keyNo: vbid.keyNo,
          note: vbid.note,
          timestamp: vbid.timestamp
        }))
      });

      // 找到並更新對應的 vbid
      const updatedVbids = vbids.map((vbid: any) => {
        if (vbid.keyNo === editingNote.keyNumber) {
          console.log('🔍 找到要更新的 vbid:', {
            keyNo: vbid.keyNo,
            oldNote: vbid.note,
            newNote: editingText.trim()
          });
          return { ...vbid, note: editingText.trim() };
        }
        return vbid;
      });

      console.log('🔍 準備更新到 Firestore:', {
        updatedVbidsCount: updatedVbids.length,
        targetKeyNumber: editingNote.keyNumber
      });

      // 更新文檔
      await updateDoc(docRef, {
        vbids: updatedVbids
      });

      console.log('✅ 筆記已保存到 Firestore');

      // 更新本地狀態
      setNotes(prevNotes => 
        prevNotes.map(videoNote => {
          if (videoNote.videoId === editingNote.videoId) {
            return {
              ...videoNote,
              notes: videoNote.notes?.map(note => 
                note.keyNumber === editingNote.keyNumber 
                  ? { ...note, note: editingText.trim() }
                  : note
              )
            };
          }
          return videoNote;
        })
      );

      // 清除編輯狀態
      setEditingNote(null);
      setEditingText('');

    } catch (error) {
      console.error('❌ 保存筆記時發生錯誤:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // 🚀 優化：全文檢索功能 - 搜尋時載入所有資料
  const performSearch = useCallback(async (keyword: string) => {
    if (!authUser?.uid || !keyword.trim()) {
      setSearchResults([]);
      setMatchCount(0);
      setCurrentMatchIndex(0);
      return;
    }

    setIsSearching(true);
    try {
      // 调试日志已移除
      
      // 🚀 優化：搜尋時載入所有資料
      const allVideoNotes = await loadAllDataForSearch();
      // 移除詳細日誌以提升性能

      const searchResults: VideoNote[] = [];
      const lowerKeyword = keyword.toLowerCase();

      // 🚀 優化：在所有已載入的資料中搜尋（包括影片標題和筆記內容）
      allVideoNotes.forEach((videoNote) => {
        // 檢查影片標題是否包含關鍵字
        const titleMatches = !!(videoNote.title && 
          videoNote.title.toLowerCase().includes(lowerKeyword));
        
        // 檢查筆記內容是否包含關鍵字
        const matchingNotes = videoNote.notes?.filter(note => 
          note.note && note.note.toLowerCase().includes(lowerKeyword)
        ) || [];
        
        // 移除詳細調試日誌以提升性能
        
        // 如果影片標題匹配或有筆記內容匹配，就加入搜尋結果
        if (titleMatches || matchingNotes.length > 0) {
          searchResults.push({
            ...videoNote,
            notes: matchingNotes.length > 0 ? matchingNotes : videoNote.notes, // 如果標題匹配但沒有筆記匹配，顯示所有筆記
            notesCount: matchingNotes.length > 0 ? matchingNotes.length : videoNote.notes?.length || 0,
            titleMatch: titleMatches // 標記是否為標題匹配
          });
        }
      });

      setSearchResults(searchResults);
      setMatchCount(searchResults.length);
    setCurrentMatchIndex(0);
      // 移除詳細日誌以提升性能
      
    } catch (error) {
      console.error('❌ 搜尋失敗:', error);
      setError('搜尋失敗，請稍後再試');
    } finally {
      setIsSearching(false);
    }
  }, [authUser?.uid, navigate]);

  // 清除搜尋
  const handleClearSearch = () => {
    setSearchKeyword('');
    setSearchResults([]);
    setMatchCount(0);
    setCurrentMatchIndex(0);
    setIsSearching(false);
    setHasSearched(false);
  };

  // 🔧 修復：使用與 ManagePages 一致的月份篩選邏輯
  const handleMonthFilterChange = async (month: string) => {
    console.log(`📅 切換月份篩選: ${month}`);
    
    const newSelectedFilters = new Set(selectedMonthFilters);
    if (newSelectedFilters.has(month)) {
      newSelectedFilters.delete(month);
      console.log(`🚀 取消選擇 ${month}，清空篩選結果`);
      setFilteredNotes([]);
    } else {
      newSelectedFilters.add(month);
      
      // 🔧 修復：使用與 ManagePages 相同的查詢邏輯
      if (authUser?.uid) {
        try {
          setLoading(true);
          console.log(`📅 載入 ${month} 月份資料，使用與 ManagePages 一致的邏輯...`);
          
          // 使用 userWatchHistoryService 進行月份篩選
          const { getUserWatchHistory } = await import('../services/userWatchHistoryService');
          const monthData = await getUserWatchHistory(authUser?.uid, 1000, month);
          
          console.log(`📊 ${month} 月份載入結果: ${monthData.length} 個記錄`);
          
          // 根據觀看記錄獲取影片詳情（與 ManagePages 相同的邏輯）
          const db = getDb();
          if (!db) {
            throw new Error('Firestore 未初始化');
          }
          
          const videoNotes: VideoNote[] = [];
          
          for (const watchRecord of monthData) {
            try {
              // 查詢 sharedVbids 獲取影片詳情，優先查詢用戶自己的影片
              let videoQuery = query(
                collection(db, 'sharedVbids'),
                where('videoId', '==', watchRecord.videoId),
                where('userId', '==', authUser?.uid)
              );
              let videoSnapshot = await getDocs(videoQuery);
              
              // 如果沒有找到用戶自己的影片，查詢任何可用的影片（包括社群影片）
              if (videoSnapshot.empty) {
                // 🔧 移除詳細日誌以減少控制台輸出
                // 调试日志已移除
                videoQuery = query(
                  collection(db, 'sharedVbids'),
                  where('videoId', '==', watchRecord.videoId)
                );
                videoSnapshot = await getDocs(videoQuery);
              }
              
              if (!videoSnapshot.empty) {
                // 🔧 修改：處理所有觀點，而不是只選擇一個
                const processedDocs = new Set<string>();
                
                for (const doc of videoSnapshot.docs) {
                  const data = doc.data();
                  
                  // 避免重複處理同一個文檔
                  if (processedDocs.has(doc.id)) {
                    continue;
                  }
                  processedDocs.add(doc.id);
                  
                  // 🔧 新增：如果有觀點名稱，加入標題中
                  let videoTitle = data.title || '';
                  if (data.viewpointName && data.viewpointName.trim() !== '') {
                    videoTitle = `${videoTitle} (${data.viewpointName})`;
                  }
                
                  videoNotes.push({
                    id: doc.id,
                    videoId: data.videoId,
                    title: videoTitle,
                    lastOpenDate: watchRecord.lastOpenDate, // 使用 watchRecord 的 lastOpenDate
                    addedDate: data.addedDate,
                    zones: data.zones || [],
                    userId: data.userId,
                    checkpointCount: data.checkpointCount || 0,
                    userLastReadAt: watchRecord.lastReadAt,
                  notes: (data.vbids || []).map((vbid: any) => {
                    let formattedTimecode = '00:00:00 00';
                    
                    if (vbid.timestamp !== null && vbid.timestamp !== undefined && vbid.timestamp >= 0 && vbid.timestamp <= 1000000) {
                      const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
                        const MAX_VIDEO_DURATION = 1000000; // 支持長時間直播，約 278 小時
                        if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
                          return '00:00:00 00';
                        }
                        
                        const pad = (num: number): string => num.toString().padStart(2, '0');
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = Math.floor(seconds % 60);
                        const frame = frameOverride !== undefined && frameOverride !== null ? frameOverride : Math.floor((seconds % 1) * 30);
                        return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frame)}`;
                      };
                      
                      formattedTimecode = formatTimecode(vbid.timestamp, vbid.frame || 0);
                    } else if (vbid.timecode && vbid.timecode.trim() !== '' && vbid.timecode !== '00:00:00 00') {
                      const timecodeRegex = /^\d{2}:\d{2}:\d{2} \d{2}$/;
                      if (timecodeRegex.test(vbid.timecode)) {
                        formattedTimecode = vbid.timecode;
                      }
                    }
                    
                    return {
                      keyNumber: vbid.keyNo,
                      note: vbid.note || '',
                      timestamp: vbid.timestamp,
                      timecode: formattedTimecode
                    };
                  }),
                  publishedAt: data.publishedAt,
                  channelName: data.channelName,
                  // 🔧 關鍵修復：雲端檔案的完整 URL 可能保存在多個字段
                  // 優先級：relativePath > url > videoUrl > path > cloudFile?.url
                  videoUrl: (() => {
                    const finalUrl = data.relativePath || data.url || data.videoUrl || data.path || data.cloudFile?.url;
                    // 🔧 調試：記錄雲端檔案的 URL 來源
                    if (data.videoId?.startsWith('file_')) {
                      console.log('🔍 [NotesPage 用戶] 雲端檔案 URL 解析:', {
                        videoId: data.videoId,
                        docId: selectedVideo.id,
                        relativePath: data.relativePath?.substring(0, 50),
                        url: data.url?.substring(0, 50),
                        videoUrl: data.videoUrl,
                        path: data.path,
                        cloudFileUrl: data.cloudFile?.url?.substring(0, 50),
                        finalUrl: finalUrl?.substring(0, 50)
                      });
                    }
                    return finalUrl;
                  })(),
                  thumbnail: data.thumbnail
                });
                }
              }
            } catch (error) {
              console.warn(`⚠️ 處理影片 ${watchRecord.videoId} 失敗:`, error);
            }
          }
          
          setFilteredNotes(videoNotes);
          console.log(`✅ ${month} 月份篩選完成: ${videoNotes.length} 個影片`);
        } catch (error) {
          console.error(`❌ 載入 ${month} 月份資料失敗:`, error);
          setFilteredNotes([]);
        } finally {
          setLoading(false);
        }
      }
    }
    
    setSelectedMonthFilters(newSelectedFilters);
  };

  // 清除月份篩選
  const handleClearMonthFilter = () => {
    setSelectedMonthFilters(new Set());
    setFilteredNotes([]);
  };

  // 生成月份篩選選項
  const generateMonthFilterOptions = useCallback(async () => {
    try {
      // 🔧 移除詳細日誌以減少控制台輸出
      // 调试日志已移除
      
      if (!authUser?.uid) {
        console.log('❌ 用戶未登入，跳過月份選項生成');
        setMonthFilterOptions([]); // Fix: Set empty array for unauthenticated users
        return;
      }
      
      // 🔧 修復：使用與 fetchUserNotes 相同的資料來源
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        console.error('❌ Firestore 未初始化');
        setMonthFilterOptions([]); // Fix: Set empty array if Firestore not initialized
        return;
      }
      
      // 🔧 修復：使用與 fetchUserNotes 相同的邏輯
      let userWatchHistory: any[] = [];
      
      // 🔧 性能優化：只查詢最近 100 個記錄（足以生成月份選項，從 1000 減少到 100）
      const watchHistoryQuery = query(
        collection(db, 'userWatchHistory'),
        where('userId', '==', authUser.uid),
        orderBy('lastOpenDate', 'desc'),
        limit(100)  // 🔧 優化：提升查詢速度 ~90%
      );
      
      const watchHistorySnapshot = await getDocs(watchHistoryQuery);
      // 🔧 移除詳細日誌以減少控制台輸出
      // console.log(`📊 個人觀看記錄載入: ${watchHistorySnapshot.size} 個記錄`);
      userWatchHistory = watchHistorySnapshot.docs.map(doc => doc.data() as any);
      
      // 如果沒有觀看記錄，查詢用戶自己的影片
      if (userWatchHistory.length === 0) {
        // 调试日志已移除
        // 🔧 性能優化：只查詢 100 個記錄（從 1000 減少到 100）
        const userVideosQuery = query(
          collection(db, 'sharedVbids'),
          where('userId', '==', authUser.uid),
          orderBy('addedDate', 'desc'),
          limit(100)  // 🔧 優化：提升查詢速度 ~90%
        );
        
        const userVideosSnapshot = await getDocs(userVideosQuery);
        console.log(`📊 用戶自己的影片載入: ${userVideosSnapshot.size} 個記錄`);
        
        // 轉換為 userWatchHistory 格式
        userWatchHistory = userVideosSnapshot.docs.map(doc => {
          const data = doc.data() as any;
          return {
            userId: authUser.uid,
            videoId: data.videoId,
            lastOpenDate: data.addedDate, // 使用 addedDate 作為 lastOpenDate
            note: data.vbids?.[0]?.note || '',
            timestamp: data.vbids?.[0]?.timestamp || 0
          };
        });
      }
      
      console.log(`📊 從 userWatchHistory 查詢到 ${userWatchHistory.length} 個記錄用於月份選項生成`);
      
      const months = new Set<string>();
      userWatchHistory.forEach((watchRecord) => {
        const lastOpenDate = watchRecord.lastOpenDate;
        if (lastOpenDate) {
          const date = new Date(lastOpenDate);
          if (!isNaN(date.getTime())) {
            const year = date.getUTCFullYear(); // Fix: Use UTC year
            const month = date.getUTCMonth() + 1; // Fix: Use UTC month
            const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
            months.add(monthStr);
            // 🔧 移除詳細日誌以減少控制台輸出
            // console.log(`🔍 處理觀看記錄:`, { videoId, lastOpenDate, parsedDate, year, month, monthStr });
          }
        }
      });
      
      const sortedMonths = Array.from(months).sort().reverse();
      console.log(`📅 從 userWatchHistory 生成月份選項: ${sortedMonths.length} 個月份`, sortedMonths);
      setMonthFilterOptions(sortedMonths); // Fix: Ensure month options are set
      if (sortedMonths.length === 0) {
        console.warn('⚠️ 沒有找到任何月份選項，用戶可能沒有觀看記錄');
      }
      
    } catch (error) {
      console.error('❌ 獲取月份選項失敗:', error);
      setMonthFilterOptions([]); // Fix: Set empty array on error
      if (error.code) {
        console.error('錯誤代碼:', error.code);
      }
      if (error.message) {
        console.error('錯誤訊息:', error.message);
      }
    }
  }, [authUser?.uid]);

  // 載入初始數據
  const fetchUserNotes = useCallback(async () => {
    try {
      // 等待 Firebase 初始化
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        setError('Firestore 未初始化');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      setHasLoadedInitialData(false); // 🔧 修復：重置載入狀態，確保重新載入數據

      // 调试日志已移除
      
      // 獲取當前月份和前一個月份
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      const prevMonth = now.getMonth() === 0 ? 
        `${now.getFullYear() - 1}-12` : 
        `${now.getFullYear()}-${now.getMonth().toString().padStart(2, '0')}`;
      
      // 移除詳細日誌以提升性能
      
      // 🔧 修復：根據登入狀態選擇查詢策略
      if (authUser?.uid) { // 已登入用戶使用觀看記錄邏輯
        try {
          // 🔧 修復：檢查是否正在載入中，避免重複載入
          const loadingKey = `userWatchHistory_${authUser?.uid || 'guest'}`;
          if ((window as any)[loadingKey]) {
            // 调试日志已移除
            // 🔧 修改：即使跳過重複載入，也要執行我們的邏輯來顯示觀點
            // 调试日志已移除
            // 不直接返回，繼續執行後面的邏輯
          }
          
          (window as any)[loadingKey] = true;
          
          // 调试日志已移除
          // 调试日志已移除
          
          // 根據觀看記錄獲取影片詳情（與 ManagePages 相同的邏輯）
          const db = getDb();
          if (!db) {
            throw new Error('Firestore 未初始化');
          }
          
          // 🔧 修復：使用混合策略查詢
          let userWatchHistory: any[] = [];
          
          if (authUser?.uid) {
            // 已登入用戶：查詢自己的觀看記錄
            // 调试日志已移除
            // 🔧 性能優化：只查詢 100 個最近記錄（足以生成月份選項，從 500 減少到 100）
            const watchHistoryQuery = query(
              collection(db, 'userWatchHistory'),
              where('userId', '==', authUser.uid),
              orderBy('lastOpenDate', 'desc'),
              limit(100)  // 🔧 優化：從 500 減少到 100，提升查詢速度 ~80%
            );
            
            const watchHistorySnapshot = await getDocs(watchHistoryQuery);
            // 🔧 移除詳細日誌以減少控制台輸出
      // console.log(`📊 個人觀看記錄載入: ${watchHistorySnapshot.size} 個記錄`);
            userWatchHistory = watchHistorySnapshot.docs.map(doc => doc.data() as any);
            
            // 🔧 修復：如果沒有觀看記錄，查詢用戶自己的影片
            if (userWatchHistory.length === 0) {
              // 调试日志已移除
              const userVideosQuery = query(
                collection(db, 'sharedVbids'),
                where('userId', '==', authUser.uid),
                orderBy('addedDate', 'desc'),
                limit(500)
              );
              
              const userVideosSnapshot = await getDocs(userVideosQuery);
              console.log(`📊 用戶自己的影片載入: ${userVideosSnapshot.size} 個記錄`);
              
              // 轉換為 userWatchHistory 格式
              userWatchHistory = userVideosSnapshot.docs.map(doc => {
                const data = doc.data() as any;
                return {
                  userId: authUser.uid,
                  videoId: data.videoId,
                  lastOpenDate: data.addedDate, // 使用 addedDate 作為 lastOpenDate
                  note: data.vbids?.[0]?.note || '',
                  timestamp: data.vbids?.[0]?.timestamp || 0
                };
              });
            }
          } else {
            // 未登入用戶：使用與 ManagePages 社群分頁相同的查詢邏輯
            // 调试日志已移除
            
            let communityQuery;
            try {
              // 優先使用 lastOpenDate 排序（與 ManagePages 一致）
              communityQuery = query(
                collection(db, 'sharedVbids'),
                where('zones', 'array-contains', 'community'),
                orderBy('lastOpenDate', 'desc'),
                limit(500)
              );
            } catch (error) {
              // 调试日志已移除
              // 如果 lastOpenDate 索引不可用，回退到 addedDate 排序（與 ManagePages 一致）
              communityQuery = query(
                collection(db, 'sharedVbids'),
                where('zones', 'array-contains', 'community'),
                orderBy('addedDate', 'desc'),
                limit(500)
              );
            }
            
            const communitySnapshot = await getDocs(communityQuery);
            console.log(`📊 社群影片載入: ${communitySnapshot.size} 個記錄`);
            
            // 轉換為 userWatchHistory 格式
            userWatchHistory = communitySnapshot.docs.map(doc => {
              const data = doc.data() as any;
              return {
                userId: 'community',
                videoId: data.videoId,
                lastOpenDate: data.lastOpenDate,
                note: data.vbids?.[0]?.note || '',
                timestamp: data.vbids?.[0]?.timestamp || 0
              };
            });
          }
          
          const videosData: VideoNote[] = [];
          
          for (const watchRecord of userWatchHistory) {
            try {
              // 查詢 sharedVbids 獲取影片詳情，優先查詢用戶自己的影片
              let videoQuery = query(
                collection(db, 'sharedVbids'),
                where('videoId', '==', watchRecord.videoId),
                where('userId', '==', authUser?.uid)
              );
              let videoSnapshot = await getDocs(videoQuery);
              
              // 如果沒有找到用戶自己的影片，查詢任何可用的影片（包括社群影片）
              if (videoSnapshot.empty) {
                // 🔧 移除詳細日誌以減少控制台輸出
                // 调试日志已移除
                videoQuery = query(
                  collection(db, 'sharedVbids'),
                  where('videoId', '==', watchRecord.videoId)
                );
                videoSnapshot = await getDocs(videoQuery);
              }
              
              if (!videoSnapshot.empty) {
                // 🔧 修復：處理所有觀點，而不是只選擇一個
                const processedDocs = new Set<string>();
                
                for (const selectedVideo of videoSnapshot.docs) {
                  // 避免重複處理同一個文檔
                  if (processedDocs.has(selectedVideo.id)) {
                    continue;
                  }
                  processedDocs.add(selectedVideo.id);
                  
                  const data = selectedVideo.data();
                
                  // 🔧 修復：使用智能標題獲取邏輯（與訪客用戶一致）
                  let videoTitle = '';
                  if (data.title && data.title.trim() !== '') {
                    videoTitle = data.title;
                  } else if (data.youtubeInfo?.title) {
                    videoTitle = data.youtubeInfo.title;
                  } else {
                    const cleanVideoId = data.videoId.replace(/^.*_/, '');
                    videoTitle = cleanVideoId;
                  }
                  
                  // 🔧 新增：如果有觀點名稱，加入標題中
                  if (data.viewpointName && data.viewpointName.trim() !== '') {
                    videoTitle = `${videoTitle} (${data.viewpointName})`;
                  }
                
                  videosData.push({
                  id: selectedVideo.id,
                  videoId: data.videoId,
                  title: videoTitle,  // 🔧 使用智能獲取的標題
                  lastOpenDate: watchRecord.lastOpenDate, // 🔧 使用觀看記錄的 lastOpenDate
                  addedDate: data.addedDate,
                  zones: data.zones || [],
                  userId: data.userId,
                  checkpointCount: data.checkpointCount || 0,
                  userLastReadAt: watchRecord.lastReadAt,
                  notes: (data.vbids || []).map((vbid: any) => {
                    let formattedTimecode = '00:00:00 00';
                    
                    if (vbid.timestamp !== null && vbid.timestamp !== undefined && vbid.timestamp >= 0 && vbid.timestamp <= 1000000) {
                      const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
                        const MAX_VIDEO_DURATION = 1000000; // 支持長時間直播，約 278 小時
                        if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
                          return '00:00:00 00';
                        }
                        
                        const pad = (num: number): string => num.toString().padStart(2, '0');
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = Math.floor(seconds % 60);
                        const frame = frameOverride !== undefined && frameOverride !== null ? frameOverride : Math.floor((seconds % 1) * 30);
                        return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frame)}`;
                      };
                      
                      formattedTimecode = formatTimecode(vbid.timestamp, vbid.frame || 0);
                    } else if (vbid.timecode && vbid.timecode.trim() !== '' && vbid.timecode !== '00:00:00 00') {
                      const timecodeRegex = /^\d{2}:\d{2}:\d{2} \d{2}$/;
                      if (timecodeRegex.test(vbid.timecode)) {
                        formattedTimecode = vbid.timecode;
                      }
                    }
                    
                    return {
                      keyNumber: vbid.keyNo,
                      note: vbid.note || '',
                      timestamp: vbid.timestamp,
                      timecode: formattedTimecode
                    };
                  }),
                  publishedAt: data.publishedAt,
                  channelName: data.channelName,
                  // 🔧 關鍵修復：雲端檔案的完整 URL 可能保存在多個字段
                  // 優先級：relativePath > url > videoUrl > path > cloudFile?.url
                  videoUrl: (() => {
                    const finalUrl = data.relativePath || data.url || data.videoUrl || data.path || data.cloudFile?.url;
                    // 雲端檔案 URL 解析日誌已移除
                    return finalUrl;
                  })(),
                  thumbnail: data.thumbnail
                  });
                } // 關閉 for 循環
              }
            } catch (error) {
              console.warn(`⚠️ 處理影片 ${watchRecord.videoId} 失敗:`, error);
            }
          }
          
          // 🔧 移除詳細日誌以減少控制台輸出
          // console.log(`📊 處理後的影片數據: ${videosData.length} 個`);
          
          setNotes(videosData);
          setHasLoadedInitialData(true);
          setLoading(false);
          
          // 🔧 修復：清除載入標記
          (window as any)[loadingKey] = false;
          return;
          
        } catch (error) {
          console.warn('⚠️ userWatchHistoryService 查詢失敗，回退到原始查詢:', error);
        }
      } else {
        // 未登入用戶：查詢社群影片
        // 调试日志已移除
        
        let communityQuery;
        try {
          communityQuery = query(
            collection(db, 'sharedVbids'),
            where('zones', 'array-contains', 'community'),
            orderBy('lastOpenDate', 'desc'),
            limit(500)
          );
        } catch (error) {
          // 调试日志已移除
          communityQuery = query(
            collection(db, 'sharedVbids'),
            where('zones', 'array-contains', 'community'),
            orderBy('addedDate', 'desc'),
            limit(500)
          );
        }
        
        const communitySnapshot = await getDocs(communityQuery);
        console.log(`📊 社群影片載入: ${communitySnapshot.size} 個記錄`);
        
        // 轉換為 VideoNote 格式
        const videosData: VideoNote[] = [];
        communitySnapshot.forEach((doc) => {
          const data = doc.data() as any;
          
          // 🔧 修復：使用與已登入用戶相同的智能標題獲取邏輯
          let videoTitle = '';
          if (data.title && data.title.trim() !== '') {
            videoTitle = data.title;
          } else if (data.youtubeInfo?.title) {
            videoTitle = data.youtubeInfo.title;
          } else {
            const cleanVideoId = data.videoId.replace(/^.*_/, '');
            videoTitle = cleanVideoId;
          }
          
          videosData.push({
            id: doc.id,
            videoId: data.videoId,
            title: videoTitle,  // 🔧 使用智能獲取的標題
            lastOpenDate: data.lastOpenDate,
            addedDate: data.addedDate,
            zones: data.zones || [],
            userId: data.userId,
            checkpointCount: data.checkpointCount || 0,
            userLastReadAt: data.userLastReadAt,
            notes: (data.vbids || []).map((vbid: any) => {
              let formattedTimecode = '00:00:00 00';
              
              if (vbid.timestamp !== null && vbid.timestamp !== undefined && vbid.timestamp >= 0 && vbid.timestamp <= 1000000) {
                const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
                  const MAX_VIDEO_DURATION = 1000000;
                  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
                    return '00:00:00 00';
                  }
                  
                  const pad = (num: number): string => num.toString().padStart(2, '0');
                  const hours = Math.floor(seconds / 3600);
                  const minutes = Math.floor((seconds % 3600) / 60);
                  const secs = Math.floor(seconds % 60);
                  const frame = frameOverride !== undefined && frameOverride !== null ? frameOverride : Math.floor((seconds % 1) * 30);
                  return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frame)}`;
                };
                
                formattedTimecode = formatTimecode(vbid.timestamp, vbid.frame || 0);
              } else if (vbid.timecode && vbid.timecode.trim() !== '' && vbid.timecode !== '00:00:00 00') {
                const timecodeRegex = /^\d{2}:\d{2}:\d{2} \d{2}$/;
                if (timecodeRegex.test(vbid.timecode)) {
                  formattedTimecode = vbid.timecode;
                }
              }
              
              return {
                keyNumber: vbid.keyNo,
                note: vbid.note || '',
                timecode: formattedTimecode,
                timestamp: vbid.timestamp || 0
              };
            }),
            publishedAt: data.publishedAt,
            channelName: data.channelName,
            // 🔧 關鍵修復：雲端檔案的完整 URL 可能保存在多個字段
            // 優先級：relativePath > url > videoUrl > path > cloudFile?.url
            videoUrl: (() => {
              const finalUrl = data.relativePath || data.url || data.videoUrl || data.path || data.cloudFile?.url;
              // 🔧 調試：記錄雲端檔案的 URL 來源
              if (data.videoId?.startsWith('file_')) {
                console.log('🔍 [NotesPage 社群] 雲端檔案 URL 解析:', {
                  videoId: data.videoId,
                  relativePath: data.relativePath?.substring(0, 50),
                  url: data.url?.substring(0, 50),
                  videoUrl: data.videoUrl,
                  path: data.path,
                  cloudFileUrl: data.cloudFile?.url?.substring(0, 50),
                  finalUrl: finalUrl?.substring(0, 50)
                });
              }
              return finalUrl;
            })(),
            thumbnail: data.thumbnail
          });
        });
        
        setNotes(videosData);
        setHasLoadedInitialData(true);
        setLoading(false);
        
        // 清除載入標記
        (window as any)[`userWatchHistory_guest`] = false;
        return;
      }
      
      
      let notesQuery;
      if (!authUser?.uid) {
        // 未登入用戶：查看社群影片筆記，按 lastOpenDate 排序
        notesQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('zones', 'array-contains', 'community'),
          orderBy('lastOpenDate', 'desc'),
          limit(500) // 🔧 優化：初始載入500筆，減少載入時間
        );
      } else {
        // 如果已登入用戶的回退方案也失敗，使用最簡單的查詢
        notesQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('userId', '==', authUser?.uid),
          orderBy('addedDate', 'desc'),
          limit(500)
        );
      }

      let notesSnapshot;
      try {
        notesSnapshot = await getDocs(notesQuery);
      } catch (error) {
        // 调试日志已移除
        // 如果 lastOpenDate 索引不可用，回退到 addedDate 排序
        let fallbackQuery;
        if (authUser?.uid) {
          // 已登入用戶的回退查詢
          fallbackQuery = query(
            collection(getDb(), 'sharedVbids'),
            where('userId', '==', authUser?.uid),
            orderBy('addedDate', 'desc'),
            limit(500)
          );
        } else {
          // 未登入用戶的回退查詢
          fallbackQuery = query(
            collection(getDb(), 'sharedVbids'),
            where('zones', 'array-contains', 'community'),
            orderBy('addedDate', 'desc'),
            limit(500)
          );
        }
        notesSnapshot = await getDocs(fallbackQuery);
      }
      // 移除詳細日誌以提升性能
      
      const videoNotes: VideoNote[] = [];
      // 🔧 修改：使用文檔ID而不是videoId來避免重複，支援同一影片的不同觀點
      const processedDocs = new Set<string>();

      // 调试日志已移除

        notesSnapshot.forEach((doc) => {
        const videoData = doc.data() as any;
        
        // 🔍 調試：記錄每個文檔的資訊
        console.log('🔍 處理文檔:', {
          docId: doc.id,
          videoId: videoData.videoId,
          viewpointName: videoData.viewpointName,
          title: videoData.title,
          vbidsCount: videoData.vbids?.length || 0
        });
        
        if (videoData.videoId && 
            videoData.videoId !== 'unknown' && 
            !videoData.videoId.includes('jeff@redball.com.tw') &&
            videoData.videoId.length >= 5 &&
            !processedDocs.has(doc.id)) {
          
          processedDocs.add(doc.id);
          
          // 🚀 修復：正確獲取影片標題，並加入觀點名稱
          let videoTitle = '';
          if (videoData.title && videoData.title.trim() !== '') {
            videoTitle = videoData.title;
          } else if (videoData.youtubeInfo?.title) {
            videoTitle = videoData.youtubeInfo.title;
          } else {
            const cleanVideoId = videoData.videoId.replace(/^.*_/, '');
            videoTitle = cleanVideoId;
          }
          
          // 🔧 新增：如果有觀點名稱，加入標題中
          if (videoData.viewpointName && videoData.viewpointName.trim() !== '') {
            videoTitle = `${videoTitle} (${videoData.viewpointName})`;
          }
          
          const allVbids = videoData.vbids || [];
          
          // 🔧 修復：移除 vbids 長度限制，顯示所有有重點的影片
          if (allVbids.length > 0) {
            const videoNote: VideoNote = {
              id: doc.id, // 🔧 修復：設置文檔 ID
              videoId: videoData.videoId,
              title: videoTitle,
              checkpointCount: videoData.vbids?.length || 0,
              addedDate: videoData.addedDate, // 🔧 修復：添加 addedDate 字段
              earliestNote: videoData.addedDate,
              lastWatchedDate: videoData.addedDate,
              lastOpenDate: videoData.lastOpenDate || videoData.addedDate, // 🔧 新增：設置瀏覽時間
              notes: allVbids.map((vbid: any) => {
                // 🔧 修復：從 timestamp 生成正確的時間碼
                let formattedTimecode = '00:00:00 00';
                
                // 優先使用 timestamp 生成時間碼（因為 VBID 中的 timecode 可能都是 00:00:00 00）
                if (vbid.timestamp !== null && vbid.timestamp !== undefined && vbid.timestamp >= 0 && vbid.timestamp <= 1000000) {
                  const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
                    const MAX_VIDEO_DURATION = 1000000; // 支持長時間直播，約 278 小時
                    if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
                      return '00:00:00 00';
                    }
                    
                    const pad = (num: number): string => num.toString().padStart(2, '0');
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    const secs = Math.floor(seconds % 60);
                    const frame = frameOverride !== undefined && frameOverride !== null ? frameOverride : Math.floor((seconds % 1) * 30);
                    return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frame)}`;
                  };
                  
                  formattedTimecode = formatTimecode(vbid.timestamp, vbid.frame || 0);
                  console.log(`✅ 從 timestamp ${vbid.timestamp} 生成時間碼: ${formattedTimecode}`);
                } else if (vbid.timecode && vbid.timecode.trim() !== '' && vbid.timecode !== '00:00:00 00') {
                  // 只有在 timestamp 無效且 timecode 不是 00:00:00 00 時，才使用 timecode
                  const timecodeRegex = /^\d{2}:\d{2}:\d{2} \d{2}$/;
                  if (timecodeRegex.test(vbid.timecode)) {
                    formattedTimecode = vbid.timecode;
                    console.log(`✅ 使用有效的 timecode: ${vbid.timecode}`);
                  }
                }
                
                console.log(`🔍 vbid 數據處理:`, {
                  keyNo: vbid.keyNo,
                  note: vbid.note,
                  timestamp: vbid.timestamp,
                  originalTimecode: vbid.timecode,
                  formattedTimecode: formattedTimecode
                });
                
                return {
                  keyNumber: vbid.keyNo,
                  note: vbid.note || '', // 🔧 修復：允許空筆記內容
                  timestamp: vbid.timestamp,
                  timecode: formattedTimecode
                };
              })
            };
            
            videoNotes.push(videoNote);
            
            // 🔧 添加調試日誌
            if (import.meta.env.DEV) {
              const addedDate = videoData.addedDate;
              let date;
              if (addedDate && typeof addedDate.toDate === 'function') {
                date = addedDate.toDate();
              } else if (addedDate) {
                date = new Date(addedDate);
              }
              const monthStr = date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}` : 'unknown';
              // 移除詳細的影片載入日誌以提升性能
            }
          }
        }
      });
      
      setNotes(videoNotes);
      setLastDoc(notesSnapshot.docs[notesSnapshot.docs.length - 1]); // 🚀 設置 lastDoc 用於分頁
      setHasMore(notesSnapshot.docs.length === 500); // 🚀 檢查是否還有更多數據
      setHasLoadedInitialData(true);
      
      console.log('✅ 最終生成', videoNotes.length, '個 VideoNote 對象');
      console.log('🔍 最終結果詳情:', videoNotes.map(note => ({
        videoId: note.videoId,
        title: note.title,
        checkpointCount: note.checkpointCount
      })));
      
    } catch (error) {
      console.error('❌ 載入筆記失敗:', error);
      setError('載入筆記失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [authUser?.uid]);

  // 🚀 優化：載入更多筆記 - 分頁載入500筆
  const loadMoreNotes = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      // 移除詳細日誌以提升性能
      
      if (!lastDoc) {
        // 移除詳細日誌以提升性能
        setHasMore(false);
        return;
      }
      
      // 🔧 修復：支持未登入用戶載入更多社群影片筆記
      let moreNotesQuery;
      if (authUser?.uid) {
        // 已登入用戶：載入更多個人筆記
        moreNotesQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('userId', '==', authUser?.uid),
          orderBy('addedDate', 'desc'),
          startAfter(lastDoc),
          limit(500) // 🚀 優化：每批載入500筆
        );
      } else {
        // 未登入用戶：載入更多社群影片筆記
        moreNotesQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('zones', 'array-contains', 'community'),
          orderBy('addedDate', 'desc'),
          startAfter(lastDoc),
          limit(500) // 🚀 優化：每批載入500筆
        );
      }
      
      const moreSnapshot = await getDocs(moreNotesQuery);
      // 移除詳細日誌以提升性能
      
      if (moreSnapshot.empty) {
        setHasMore(false);
        return;
      }
      
      const newVideoNotes: VideoNote[] = [];
      // 🔧 修改：使用文檔ID而不是videoId來避免重複，支援同一影片的不同觀點
      const newProcessedDocs = new Set<string>();
      
      moreSnapshot.forEach((doc) => {
        const videoData = doc.data() as any;
        
        if (videoData.videoId && 
            videoData.videoId !== 'unknown' && 
            !videoData.videoId.includes('jeff@redball.com.tw') &&
            videoData.videoId.length >= 5 &&
            !newProcessedDocs.has(doc.id)) {
          
          newProcessedDocs.add(doc.id);
          
          let videoTitle = '';
          if (videoData.title && videoData.title.trim() !== '' && videoData.title !== 'undefined') {
            videoTitle = videoData.title;
          } else if (videoData.youtubeInfo?.title) {
            videoTitle = videoData.youtubeInfo.title;
          } else {
            const cleanVideoId = videoData.videoId.replace(/^.*_/, '');
            videoTitle = cleanVideoId;
          }
          
          // 🔧 新增：如果有觀點名稱，加入標題中
          if (videoData.viewpointName && videoData.viewpointName.trim() !== '') {
            videoTitle = `${videoTitle} (${videoData.viewpointName})`;
          }
          
          const allVbids = videoData.vbids || [];
          
          if (allVbids.length > 0) {
            const videoNote: VideoNote = {
              videoId: videoData.videoId,
              title: videoTitle,
              checkpointCount: videoData.vbids?.length || 0,
              addedDate: videoData.addedDate,
              earliestNote: videoData.addedDate,
              lastWatchedDate: videoData.addedDate,
              notes: allVbids.map((vbid: any) => ({
                keyNumber: vbid.keyNo,
                note: vbid.note || '',
                timestamp: vbid.timestamp,
                timecode: vbid.timecode || '00:00:00 00'
              }))
            };
            
            newVideoNotes.push(videoNote);
          }
        }
      });
      
      setNotes(prevNotes => [...prevNotes, ...newVideoNotes]);
      setLastDoc(moreSnapshot.docs[moreSnapshot.docs.length - 1]);
      setHasMore(moreSnapshot.docs.length === 500);
      
    } catch (error) {
      console.error('❌ 載入更多筆記失敗:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, lastDoc, authUser?.uid]);

  // 🚀 優化：月份篩選查詢 - 按需載入特定月份的所有資料
  const loadMonthData = useCallback(async (month: string) => {
    try {
      // 移除詳細日誌以提升性能
      
      const year = parseInt(month.split('-')[0]);
      const monthNum = parseInt(month.split('-')[1]);
      
      // 創建月份開始和結束日期
      const startDate = new Date(year, monthNum - 1, 1);
      const endDate = new Date(year, monthNum, 0, 23, 59, 59, 999);
      
      // 移除詳細日誌以提升性能
      
      // 🔧 修復：支持未登入用戶的月份篩選查詢
      let monthQuery;
      if (authUser?.uid) {
        // 已登入用戶：查詢個人筆記
        monthQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('userId', '==', authUser?.uid),
          orderBy('addedDate', 'desc'),
          limit(1000) // 載入足夠的資料以確保包含該月份的所有數據
        );
      } else {
        // 未登入用戶：查詢社群影片筆記
        monthQuery = query(
          collection(getDb(), 'sharedVbids'),
          where('zones', 'array-contains', 'community'),
          orderBy('addedDate', 'desc'),
          limit(1000) // 載入足夠的資料以確保包含該月份的所有數據
        );
      }
      
      const monthSnapshot = await getDocs(monthQuery);
      // 移除詳細日誌以提升性能
      
      const monthVideoNotes: VideoNote[] = [];
      const processedVideos = new Set<string>();
      
      monthSnapshot.forEach((doc) => {
        const videoData = doc.data() as any;
        
        // 🔧 修復：先檢查日期是否在目標月份範圍內
        const addedDate = videoData.addedDate;
        let date;
        
        if (addedDate && typeof addedDate.toDate === 'function') {
          date = addedDate.toDate();
        } else if (addedDate) {
          date = new Date(addedDate);
        }
        
        // 移除詳細調試日誌以提升性能
        
        // 檢查日期是否在目標月份範圍內
        if (date && date >= startDate && date <= endDate) {
          if (videoData.videoId && 
              videoData.videoId !== 'unknown' && 
              !videoData.videoId.includes('jeff@redball.com.tw') &&
              videoData.videoId.length >= 5 &&
              !processedVideos.has(videoData.videoId)) {
            
            processedVideos.add(videoData.videoId);
            
            let videoTitle = '';
            if (videoData.title && videoData.title.trim() !== '') {
              videoTitle = videoData.title;
            } else if (videoData.youtubeInfo?.title) {
              videoTitle = videoData.youtubeInfo.title;
            } else {
              const cleanVideoId = videoData.videoId.replace(/^.*_/, '');
              videoTitle = cleanVideoId;
            }
            
            const allVbids = videoData.vbids || [];
            
            if (allVbids.length > 0) {
              const videoNote: VideoNote = {
                videoId: videoData.videoId,
                title: videoTitle,
                checkpointCount: videoData.vbids?.length || 0,
                addedDate: videoData.addedDate,
                earliestNote: videoData.addedDate,
                lastWatchedDate: videoData.addedDate,
                notes: allVbids.map((vbid: any) => ({
                  keyNumber: vbid.keyNo,
                  note: vbid.note || '',
                  timestamp: vbid.timestamp,
                  timecode: vbid.timecode || '00:00:00 00'
                }))
              };
              
              monthVideoNotes.push(videoNote);
            }
          }
        }
      });
      
      // 移除詳細日誌以提升性能
      return monthVideoNotes;
      
    } catch (error) {
      console.error(`❌ 載入 ${month} 月份資料失敗:`, error);
      return [];
    }
  }, [authUser?.uid]);

  // 🚀 優化：搜尋時載入所有資料 - 使用分頁查詢確保載入所有資料
  const loadAllDataForSearch = useCallback(async () => {
    try {
      // 移除詳細日誌以提升性能
      
      const allVideoNotes: VideoNote[] = [];
      const processedVideos = new Set<string>();
      let lastDoc = null;
      let totalLoaded = 0;
      
      // 🔧 修復：使用分頁查詢確保載入所有資料
      while (true) {
        let batchQuery;
        if (lastDoc) {
          if (authUser?.uid) {
            // 已登入用戶：查詢個人筆記
            batchQuery = query(
              collection(getDb(), 'sharedVbids'),
              where('userId', '==', authUser?.uid),
              orderBy('addedDate', 'desc'),
              startAfter(lastDoc),
              limit(500)
            );
          } else {
            // 未登入用戶：查詢社群影片筆記
            batchQuery = query(
              collection(getDb(), 'sharedVbids'),
              where('zones', 'array-contains', 'community'),
              orderBy('addedDate', 'desc'),
              startAfter(lastDoc),
              limit(500)
            );
          }
        } else {
          if (authUser?.uid) {
            // 已登入用戶：查詢個人筆記
            batchQuery = query(
              collection(getDb(), 'sharedVbids'),
              where('userId', '==', authUser?.uid),
              orderBy('addedDate', 'desc'),
              limit(500)
            );
          } else {
            // 未登入用戶：查詢社群影片筆記
            batchQuery = query(
              collection(getDb(), 'sharedVbids'),
              where('zones', 'array-contains', 'community'),
              orderBy('addedDate', 'desc'),
              limit(500)
            );
          }
        }
        
        const batchSnapshot: any = await getDocs(batchQuery);
        // 移除詳細日誌以提升性能
        
        if (batchSnapshot.empty) {
          break; // 沒有更多資料了
        }
        
        totalLoaded += batchSnapshot.size;
        lastDoc = batchSnapshot.docs[batchSnapshot.docs.length - 1];
        
        // 處理這個批次的資料
        batchSnapshot.forEach((doc: any) => {
        const videoData = doc.data() as any;
        
        if (videoData.videoId && 
            videoData.videoId !== 'unknown' && 
            !videoData.videoId.includes('jeff@redball.com.tw') &&
            videoData.videoId.length >= 5 &&
            !processedVideos.has(videoData.videoId)) {
          
          processedVideos.add(videoData.videoId);
          
          let videoTitle = '';
          if (videoData.title && videoData.title.trim() !== '' && videoData.title !== 'undefined') {
            videoTitle = videoData.title;
          } else if (videoData.youtubeInfo?.title) {
            videoTitle = videoData.youtubeInfo.title;
          } else {
            const cleanVideoId = videoData.videoId.replace(/^.*_/, '');
            videoTitle = cleanVideoId;
          }
          
          const allVbids = videoData.vbids || [];
          
          if (allVbids.length > 0) {
            const videoNote: VideoNote = {
              videoId: videoData.videoId,
              title: videoTitle,
              checkpointCount: videoData.vbids?.length || 0,
              addedDate: videoData.addedDate,
              earliestNote: videoData.addedDate,
              lastWatchedDate: videoData.addedDate,
              notes: allVbids.map((vbid: any) => ({
                keyNumber: vbid.keyNo,
                note: vbid.note || '',
                timestamp: vbid.timestamp,
                timecode: vbid.timecode || '00:00:00 00'
              }))
            };
            
            allVideoNotes.push(videoNote);
          }
        }
        });
      }
      
      // 移除詳細日誌以提升性能
      return allVideoNotes;
      
    } catch (error) {
      console.error('❌ 搜尋模式載入失敗:', error);
      return [];
    }
  }, [authUser?.uid]);

  // 🚀 優化：無限滾動監聽器 - 修復 SPA 模式下的滾動問題
  useEffect(() => {
    const handleScroll = () => {
      // 🔧 修復：在 SPA 模式下，使用正確的滾動容器
      const scrollContainer = isEmbedded ? 
        document.querySelector('[data-testid="main-content"]') || document.documentElement :
        document.documentElement;
      
      const scrollTop = scrollContainer.scrollTop || window.pageYOffset;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight || window.innerHeight;
      
      // 當滾動到距離底部 500px 時觸發載入更多
      if (scrollTop + clientHeight >= scrollHeight - 500) {
        if (hasMore && !loadingMore && selectedMonthFilters.size === 0 && !searchKeyword) {
          // 调试日志已移除
          loadMoreNotes();
        }
      }
    };

    // 🔧 修復：在 SPA 模式下監聽正確的滾動容器
    const scrollContainer = isEmbedded ? 
      document.querySelector('[data-testid="main-content"]') :
      window;
    
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, loadingMore, selectedMonthFilters.size, searchKeyword, loadMoreNotes, isEmbedded]);

  // 🔧 修復：統一的數據載入邏輯，避免重複載入
  const loadingRef = useRef(false); // 防止重複載入
  
  useEffect(() => {
    // 🔧 防止重複載入
    if (loadingRef.current) {
      console.log('⏸️ 數據載入中，跳過重複請求');
      return;
    }
    
    // 🔧 減少日誌頻率，只在重要狀態變化時輸出
    if (!authLoading) {
      console.log('🚀 認證狀態已確定，開始載入數據...', { 
        authUserUid: authUser?.uid || 'guest',
        notesLength: notes.length,
        isEmbedded
      });
      
      loadingRef.current = true; // 設置載入鎖
      
      // 🔧 優化：只載入必要的數據，避免重複載入
      const loadData = async () => {
        try {
          await Promise.all([
            fetchUserNotes(),
            generateMonthFilterOptions()
          ]);
        } catch (error) {
          console.error('❌ 載入數據失敗:', error);
        } finally {
          loadingRef.current = false; // 釋放載入鎖
        }
      };
      
      loadData();
    }
  }, [authLoading, authUser?.uid, isEmbedded]);

  // 🔧 新增：調試月份篩選選項的載入狀態（減少日誌頻率）
  useEffect(() => {
    // 🔧 只在重要狀態變化時輸出日誌
    if (monthFilterOptions.length > 0) {
      console.log('🔍 月份篩選選項載入完成:', {
        monthFilterOptionsLength: monthFilterOptions.length,
        months: monthFilterOptions.slice(0, 3), // 只顯示前3個月
        authUser: !!authUser,
        hasLoadedInitialData
      });
    }
  }, [monthFilterOptions.length, authUser, hasLoadedInitialData]);

  // 強制重新計算 sticky 定位 - 在資料載入後執行
  useLayoutEffect(() => {
    if (notes.length > 0) {
      // 延遲執行，確保 DOM 完全更新
      setTimeout(() => {
        const stickyElements = document.querySelectorAll('[data-date]');
        stickyElements.forEach(element => {
          const htmlElement = element as HTMLElement;
          // 觸發重新計算 sticky 定位
          htmlElement.style.position = 'static';
          htmlElement.offsetHeight; // 強制重排
          htmlElement.style.position = 'sticky';
        });
      }, 100);
    }
  }, [notes.length]);

  // 高亮關鍵字函數
  const highlightKeyword = (text: string, keyword: string) => {
    if (!keyword.trim()) return text;
    
    const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === keyword.toLowerCase() ? 
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span> : part
    );
  };

  // 格式化日期顯示
  const formatDate = (dateString: string) => {
    try {
    const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0].replace(/-/g, '/');
      }
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    } catch (error) {
      return new Date().toISOString().split('T')[0].replace(/-/g, '/');
    }
  };

  // 格式化月份顯示
  const formatMonthDisplay = (month: string) => {
    return month.replace('-', '/');
  };

  // 渲染檢查點
  const renderCheckpoints = (videoNote: VideoNote) => {
    const notes = videoNote.notes || [];
    
    if (notes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
          (No CheckPoint)
        </Typography>
      );
    }

    // 按 keyNumber 排序
    const sortedNotes = notes.sort((a, b) => a.keyNumber - b.keyNumber);

    return (
      <Box sx={{ mt: 1 }}>
        {sortedNotes.map((note, index) => {
          const keyNumber = note.keyNumber;
          const isEditing = editingNote?.videoId === videoNote.videoId && editingNote?.keyNumber === keyNumber;
          
          return (
            <Box key={`${videoNote.videoId}-${keyNumber}-${index}`} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="body2" 
                component="span" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#1976d2',
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': {
                    color: '#1565c0',
                    textDecoration: 'underline'
                  }
                }}
                onClick={() => handlePlayVideo(videoNote, keyNumber)}
              >
                NO{keyNumber}:
              </Typography>
              
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <TextField
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        py: 0.5
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveEdit();
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    autoFocus
                  />
                  <IconButton 
                    size="small" 
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    color="primary"
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography 
                  variant="body2" 
                  component="span" 
                  sx={{ 
                    flex: 1,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                  onClick={() => handleStartEdit(videoNote.videoId, keyNumber, note.note || '')}
                >
                  {note.note ? (searchKeyword ? highlightKeyword(note.note, searchKeyword) : note.note) : '(無筆記內容)'}
              </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // 渲染筆記列表 - 使用原來的漂亮UI設計
  const renderNotes = (notesToRender: VideoNote[]) => {
    if (notesToRender.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {searchKeyword ? '找不到匹配的筆記' : '暫無筆記'}
          </Typography>
        </Box>
      );
    }

    // 按日期分組
    const groups: { [date: string]: VideoNote[] } = {};
    notesToRender.forEach((note) => {
      try {
        // 🔧 修復：使用 lastOpenDate 進行日期分組，與瀏覽記錄保持一致
        let dateStr = note.lastOpenDate || note.lastWatchedDate || note.timestamp;
        if (!dateStr || dateStr === '') {
          dateStr = new Date().toISOString();
        }
        
        // 🔧 修復：處理 Firestore Timestamp 物件
        if (dateStr && typeof dateStr === 'object' && 'seconds' in dateStr) {
          // 這是 Firestore Timestamp 物件，轉換為 ISO 字串
          dateStr = new Date((dateStr as any).seconds * 1000).toISOString();
        } else if (dateStr && typeof dateStr === 'object' && 'toDate' in dateStr) {
          // 這是 Firestore Timestamp 物件，使用 toDate() 方法
          dateStr = (dateStr as any).toDate().toISOString();
        }
        
        let date;
        if (dateStr.includes('T')) {
          date = dateStr.split('T')[0];
        } else if (dateStr.includes(' ')) {
          date = new Date(dateStr).toISOString().split('T')[0];
        } else {
          date = new Date(dateStr).toISOString().split('T')[0];
        }
        
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(note);
      } catch (error) {
        const fallbackDate = new Date().toISOString().split('T')[0];
        if (!groups[fallbackDate]) {
          groups[fallbackDate] = [];
        }
        groups[fallbackDate].push(note);
      }
    });

    const sortedDates = Object.keys(groups).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    
    return sortedDates.map((date, dateIndex) => (
      <Box key={date}>
        {/* 日期標題 - 使用 sticky 定位 */}
        <Box 
          sx={{
            position: 'sticky',
            top: { xs: 64, sm: 64 }, // 🔧 修復：貼齊 AppBar 下方邊界
            zIndex: 9,
            backgroundColor: '#e9ecef', // 更淺的灰色背景
            borderBottom: '1px solid #dee2e6',
            borderTop: '1px solid #dee2e6',
            py: { xs: 0.5, sm: 0.75 }, // 縮減內邊距
            px: { xs: 1, sm: 2 },
            mb: 0.5, // 縮減外邊距
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)', // 減輕陰影
            // 與上下容器寬度保持一致，移除負邊距
            mx: 0,
            // 手機版文字置中
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-start' } // 手機版置中，桌面版左對齊
          }}
          data-date={date} // 調試用：標記日期
        >
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            color: '#495057', // 深灰色文字，在淺背景上更清晰
            fontSize: { xs: '1.1rem', sm: '1.25rem' }, // 調整字體大小
            textAlign: { xs: 'center', sm: 'left' } // 手機版置中，桌面版左對齊
          }}>
            {formatDate(date)}
          </Typography>
        </Box>
        
        {/* 該日期的筆記內容 */}
        <Paper sx={{ mb: 3, p: 3, borderRadius: 2, boxShadow: 1 }}>
        
        <Divider sx={{ mb: 2 }} />
        
          {groups[date].map((note, index) => (
            <Box key={`${date}-${note.videoId}-${index}`} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'flex-start', lineHeight: 1.4, minHeight: 'auto', overflow: 'visible' }}>
                <Box component="span" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexShrink: 0,
                  minWidth: 'fit-content',
                  marginRight: '8px',
                  padding: '2px 0',
                  lineHeight: 1,
                  overflow: 'visible'
                }}>
                  #{index + 1}
                  {renderVideoSourceIcon(note.videoUrl || '')}
                </Box>
                <Box 
                  component="span" 
                  sx={{ 
                    flex: 1, 
                    lineHeight: 1.2, 
                    overflow: 'visible',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#1976d2',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => handlePlayVideo(note)}
                >
                  {searchKeyword ? highlightKeyword(note.title, searchKeyword) : note.title}
                </Box>
                </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {note.publishedAt && (() => {
                  try {
                    let publishedDate;
                    if (typeof note.publishedAt === 'string') {
                      publishedDate = note.publishedAt;
                    } else if (note.publishedAt && typeof (note.publishedAt as any).toDate === 'function') {
                      // Firestore Timestamp
                      publishedDate = (note.publishedAt as any).toDate().toISOString();
                    } else if (note.publishedAt && typeof (note.publishedAt as any).seconds === 'number') {
                      // Firestore Timestamp object
                      publishedDate = new Date((note.publishedAt as any).seconds * 1000).toISOString();
                    } else {
                      publishedDate = new Date(note.publishedAt).toISOString();
                    }
                    return `${publishedDate.split('T')[0].replace(/-/g, '/')}`;
                  } catch (error) {
                    console.warn('⚠️ 無法解析 publishedAt:', note.publishedAt, error);
                    return '';
                  }
                })()}
                {note.channelName && ` / ${searchKeyword ? highlightKeyword(note.channelName, searchKeyword) : note.channelName}`}
                </Typography>
              
              {renderCheckpoints(note)}
              
              {index < groups[date].length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
          ))}
        </Paper>
      </Box>
    ));
  };

  // 🚀 優化：計算過濾後的匹配數量
  const getFilteredMatchCount = () => {
    if (searchKeyword && searchResults.length > 0 && selectedMonthFilters.size > 0) {
      const filteredSearchResults = searchResults.filter(videoNote => {
        const addedDate = videoNote.addedDate;
        let date;
        
        if (addedDate && typeof addedDate.toDate === 'function') {
          date = addedDate.toDate();
        } else if (addedDate) {
          date = new Date(addedDate);
        }
        
        if (date && !isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
          return selectedMonthFilters.has(monthStr);
        }
        
        return false;
      });
      
      return filteredSearchResults.length;
    }
    
    return matchCount;
  };

  // 🚀 優化：渲染篩選後的筆記 - 支援搜尋 + 月份篩選組合
  const renderFilteredNotes = () => {
    // 🔧 修復：處理搜尋 + 月份篩選的組合
    if (searchKeyword && searchResults.length > 0) {
      // 如果有月份篩選，對搜尋結果進行月份過濾
      if (selectedMonthFilters.size > 0) {
        // 移除詳細日誌以提升性能
        
        const filteredSearchResults = searchResults.filter(videoNote => {
          const addedDate = videoNote.addedDate;
          let date;
          
          if (addedDate && typeof addedDate.toDate === 'function') {
            date = addedDate.toDate();
          } else if (addedDate) {
            date = new Date(addedDate);
          }
          
          if (date && !isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
            return selectedMonthFilters.has(monthStr);
          }
          
          return false;
        });
        
        // 移除詳細日誌以提升性能
        return renderNotes(filteredSearchResults);
      }
      
      // 只有搜尋，沒有月份篩選
      return renderNotes(searchResults);
    }
    
    // 🔧 修復：如果有月份篩選，使用 filteredNotes 狀態
    if (selectedMonthFilters.size > 0) {
      // 移除詳細日誌以提升性能
      return renderNotes(filteredNotes);
    }
    
    // 🔧 修復：所有用戶都顯示所有筆記，讓用戶可以通過月份篩選查看
    // 🔧 移除重複日誌以減少控制台輸出
    // console.log(`📅 顯示所有筆記 ${notes.length} 個`);
    // 🔧 移除詳細日誌以減少控制台輸出（避免輸出491個影片的詳細信息）
    // console.log(`📅 筆記詳情:`, notes.map(note => ({ videoId, title, lastOpenDate })));
    
    return renderNotes(notes);
  };

  // 導航到首頁
  const handleNavigateToHome = () => {
    navigate('/');
  };

  // 處理搜尋變更
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  // 手動搜尋
  const handleManualSearch = () => {
    setHasSearched(true);
    performSearch(searchKeyword);
  };

  // 清除月份篩選
  const clearMonthFilter = () => {
    setSelectedMonthFilters(new Set());
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          載入筆記中...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (notes.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          尚無筆記記錄
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          開始觀看影片並製作筆記吧！
        </Typography>
      </Box>
    );
  }

  // 搜尋結果為空時的提示
  if (searchKeyword.trim().length >= 2 && !isSearching && hasSearched && searchResults.length === 0) {
  return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          找不到匹配的筆記
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          嘗試使用不同的關鍵字搜尋
        </Typography>
        <Button 
          variant="outlined" 
          onClick={handleClearSearch}
          sx={{ mt: 2 }}
        >
          清除搜尋
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* AppBar 置於最外層，支援滑動隱藏 */}
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          color="default" 
          elevation={1} 
          sx={{ 
            backgroundColor: '#1976d2',
            zIndex: 1100,
            width: '100%',
            top: 0
          }}
        >
          <Toolbar sx={{ 
            position: 'relative',
            minHeight: '55px !important',
            height: '55px',
            padding: '0 16px'
          }}>
            {/* Logo 和文字在 AppBar 左側 */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={handleNavigateToHome}>
              {isMobile && (
                <img 
                  src="/logo.png" 
                  alt="Check Play Logo" 
                  style={{ 
                    width: '28px',
                    height: 'auto',
                    marginRight: '8px'
                  }} 
                />
              )}
              <Typography
                variant="subtitle1"
                component="div"
                sx={{
                  flexGrow: 1,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '1rem',
                  '& .english': {
                    color: '#FFFFFF',
                    display: isMobile ? 'inline' : 'none'
                  },
                  '& .chinese': {
                    color: '#FFFFFF',
                    marginLeft: isMobile ? '4px' : '0'
                  },
                  '& .bold-text': {
                    fontWeight: 'bold',
                    color: '#FFFFFF'
                  }
                }}
              >
                <span className="english">CHECK PLAY</span>
                <span className="chinese">{isMobile ? '｜' : ''}<span className="bold-text">找重點</span></span>
              </Typography>
            </Box>
            
            {/* 右側用戶信息或登入按鈕 */}
            <Box
              sx={{
                position: 'absolute',
                right: 24,
                top: 0,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1300
              }}
            >
              {authUser ? (
                <>
                  <Typography variant="body2" sx={{ mr: 0.1, color: 'white', fontSize: '0.875rem' }}>
                    Hi {(authUser.name || authUser.email || '').slice(0, 4)}
                  </Typography>
                  <Button 
                    variant="text"
                    onClick={() => {
                      // 使用 Firebase Auth 的 signOut 方法
                      import('firebase/auth').then(({ signOut, getAuth }) => {
                        const auth = getAuth();
                        signOut(auth).then(() => {
                          console.log('用戶已登出');
                          navigate('/');
                        }).catch((error) => {
                          console.error('登出失敗:', error);
                        });
                      });
                    }} 
                    sx={{ 
                      mr: 0.5,
                      px: 0.5,
                      py: 0,
                      minWidth: 'auto',
                      color: 'white',
                      fontSize: '0.75rem',
                      height: 'auto',
                      textDecoration: 'underline',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'none'
                      }
                    }}
                  >
                    登出
                  </Button>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <Button 
                    color="inherit" 
                    onClick={() => navigate('/manage')} 
                    sx={{ color: '#fff', minWidth: 0, px: 1, fontSize: '0.95rem' }}
                  >
                    登入
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      {/* 主要內容區域，添加 top padding 避免被 AppBar 遮擋 */}
      <Box sx={{ 
        pt: { xs: 5, sm: 7 }, 
        pb: { xs: 6, sm: 8 },
        minHeight: 'calc(100vh - 140px)', // 為 BottomBar 留出空間
        position: 'relative'
      }}>
    <Box sx={{ 
      p: { xs: 0.5, sm: 2, md: 3 }, 
      maxWidth: { xs: '100%', sm: '100%', md: '1200px' }, 
      mx: { xs: 0, sm: 0, md: 'auto' },
      width: '100%'
    }}>
      <style>
        {`
          .current-match {
            background-color: #ff9800 !important;
            animation: pulse 1s ease-in-out;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      
          {/* 固定頂部區域 */}
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 10, 
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: { xs: 1, sm: 2 },
            mb: { xs: 1, sm: 2 }
          }}>
            {/* 標題和搜尋功能區域 - 同一行 */}
            <Paper sx={{ p: { xs: 1, sm: 1.5 }, mb: { xs: 0.25, sm: 0.5 }, borderRadius: 2, boxShadow: 1, mt: { xs: 2, sm: 3 } }}>
              {/* 標題和搜尋框在同一行 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 0.5, sm: 1 }, 
                flexDirection: { xs: 'column', sm: 'row' },
                mb: { xs: 1, sm: 2 },
                flexWrap: 'wrap'
              }}>
                {/* 搜尋區域 - 三個元素在同一行 */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 0.5, sm: 1 },
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  width: '100%'
                }}>
                  {/* 標題 */}
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: 'primary.main',
                      flexShrink: 0,
                      fontSize: { xs: '1rem', sm: '1.5rem' },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {authUser ? '找重點' : '社群重點'}
                  </Typography>

                  {/* 搜尋輸入框 */}
          <TextField
            variant="outlined"
                    placeholder="輸入關鍵字..."
            value={searchKeyword}
            onChange={handleSearchChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleManualSearch();
                      }
                    }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                          <SearchIcon color="action" sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                </InputAdornment>
              ),
              endAdornment: searchKeyword && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                            <ClearIcon sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
                    sx={{ 
                      minWidth: { xs: 120, sm: 200 },
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        height: { xs: '40px', sm: '56px' },
                        fontSize: { xs: '14px', sm: '16px' }
                      }
                    }}
                  />
                  
                  {/* 搜尋按鈕 */}
                  <Button
                    variant="contained"
                    onClick={handleManualSearch}
                    disabled={searchKeyword.trim().length < 2 || isSearching}
                    startIcon={isSearching ? <CircularProgress size={16} /> : null}
                    sx={{ 
                      minWidth: { xs: '60px', sm: '80px' },
                      px: { xs: 1, sm: 2 },
                      height: { xs: '40px', sm: '56px' },
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 'bold',
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isSearching ? '搜尋中' : '送出'}
                  </Button>
                  
                  {/* 更新按鈕 */}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // 清除搜尋關鍵字
                      setSearchKeyword('');
                      // 重新載入所有筆記
                      fetchUserNotes();
                      // 清除搜尋結果
                      setSearchResults([]);
                      setMatchCount(0);
                      setCurrentMatchIndex(0);
                    }}
                    sx={{ 
                      minWidth: { xs: '60px', sm: '80px' },
                      px: { xs: 1, sm: 2 },
                      height: { xs: '40px', sm: '56px' },
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 'bold',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.light',
                        color: 'primary.dark'
                      }
                    }}
                  >
                    更新
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* 🆕 新增：月份快速篩選按鈕 - 僅對已登入用戶顯示 */}
            {authUser?.uid && (
              <Paper sx={{ p: { xs: 1, sm: 1.5 }, mb: { xs: 0.5, sm: 1.5 }, borderRadius: 2, boxShadow: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 0.5, sm: 1 }, 
                  mb: { xs: 0.5, sm: 1 },
                  flexWrap: 'wrap'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary', flexShrink: 0 }}>
                    月份篩選
                  </Typography>
                  {/* 選中月份提示 */}
                  {selectedMonthFilters.size > 0 && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      已選擇 {selectedMonthFilters.size} 個月：{Array.from(selectedMonthFilters).map(month => formatMonthDisplay(month)).join('、')}
                    </Typography>
                  )}
                </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 0.25, sm: 0.5 }, 
                flexWrap: 'nowrap', // 不換行
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-start' }, // 手機版也左對齊
                overflowX: 'auto', // 水平滾動
                pt: { xs: 0.25, sm: 0.5 }, // 減少上方間距
                pb: { xs: 0.25, sm: 0.5 }, // 減少下方間距
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '2px',
                }
              }}>
                {monthFilterOptions.length > 0 ? monthFilterOptions.map((month, index) => (
                  <Button
                    key={`month-${month}-${index}`}
                    variant={selectedMonthFilters.has(month) ? 'contained' : 'outlined'}
                  size="small"
                    onClick={() => handleMonthFilterChange(month)}
                    sx={{ 
                      minWidth: 'auto', 
                      px: { xs: 1, sm: 2 }, // 手機版進一步減少內邊距
                      position: 'relative',
                      flexShrink: 0, // 防止按鈕被壓縮
                      '&:after': selectedMonthFilters.has(month) ? {
                        content: '"✓"',
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 16,
                        height: 16,
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      } : {}
                    }}
                  >
                    {formatMonthDisplay(month)}
                  </Button>
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    正在載入月份選項...
                  </Typography>
                )}
                {selectedMonthFilters.size > 0 && (
                  <Button
                    variant="outlined"
                  size="small"
                    onClick={clearMonthFilter}
                    sx={{ minWidth: 'auto', px: 2, ml: 1 }}
                  >
                    清除篩選
                  </Button>
          )}
        </Box>
      </Paper>
            )}
          </Box>

          {/* 載入狀態 */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* 搜尋結果或正常筆記列表 */}
              {searchKeyword && hasSearched ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {isSearching ? (
                      <>
                        🔍 查詢中...
                        {selectedMonthFilters.size > 0 && (
                          <span style={{ color: '#666', fontSize: '0.9em', marginLeft: '8px' }}>
                            (已篩選月份: {Array.from(selectedMonthFilters).join(', ')})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        🔍 搜尋結果: {getFilteredMatchCount()} 個匹配
                        {selectedMonthFilters.size > 0 && (
                          <span style={{ color: '#666', fontSize: '0.9em', marginLeft: '8px' }}>
                            (已篩選月份: {Array.from(selectedMonthFilters).join(', ')})
                          </span>
                        )}
                      </>
                    )}
          </Typography>
                  {renderFilteredNotes()}
                </Box>
              ) : (
                renderFilteredNotes()
              )}
            </>
          )}

          {/* 載入更多按鈕 */}
          {hasMore && notes.length > 0 && !loadingMore && (
            <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={loadMoreNotes}
                disabled={loadingMore}
                sx={{ minWidth: 120 }}
              >
                {loadingMore ? '載入中...' : '載入更多'}
              </Button>
            </Box>
          )}

          {/* 載入更多狀態 */}
          {loadingMore && (
            <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary" component="span">
                載入更多筆記中...
              </Typography>
            </Box>
          )}

          {/* 沒有更多內容的提示 */}
          {!hasMore && notes.length > 0 && (
            <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                已顯示所有筆記 (共 {notes.length} 筆)
              </Typography>
            </Box>
          )}
                  
          {/* 預留底部空間，避免內容被底部Bar遮住 */}
          <Box sx={{ height: { xs: 48, sm: 48 } }} />
            </Box>
    </Box>
        
      {/* 固定在底部的 BottomBar */}
      <BottomBar menuItems={menuItems} />
    </>
  );
};

export default NotesPage;
